import { useQuery } from '@apollo/client'
import { PlayArrow } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import { PlayButton } from 'app/_components'
import { ReactNode, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { GetCurrentUserDocument, GetVideoPreviewModalQuery } from '~/__generated__/graphql'
import { SvgAsset } from '~/components/SvgAsset'
import { Colors } from '~/libs/mui/color'
import { ShareModal } from '../../ShareModal'

interface VideoButtonContentProps {
  onClickVideoPlay: () => void
  onClickAddPlaylist: () => void
  /** 近日公開予定動画であるかどうか */
  isUpComing: boolean
  videoId: string
  playlistId?: string
  videoPreviewModalInfo: GetVideoPreviewModalQuery['originalVideo']
}

const IconButon = ({ children, onClick }: { children: ReactNode; onClick: () => void }) => {
  return (
    <Button
      sx={{
        flexGrow: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 0,
        minWidth: '48px',
        width: '48px',
        height: '48px',
        gap: '4px',
        color: Colors.color5A5957,
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

export const VideoButtonContent = ({
  videoId,
  playlistId,
  onClickVideoPlay,
  onClickAddPlaylist,
  isUpComing,
  videoPreviewModalInfo,
}: VideoButtonContentProps) => {
  const [openShareModal, setOpenShareModal] = useState(false)
  const { t } = useTranslation()
  const { data: currentUserData } = useQuery(GetCurrentUserDocument)
  const isLoggedIn = useMemo(() => {
    return !!currentUserData?.currentUser?.id
  }, [currentUserData])
  const isInPlaylist = useMemo(
    () => (videoPreviewModalInfo?.playlistsVideos?.length || 0) > 0,
    [videoPreviewModalInfo],
  )

  return (
    <Box display='flex' gap={2}>
      {!isUpComing && (
        <>
          {isLoggedIn ? (
            <>
              <PlayButton videoId={videoId} playlistId={playlistId} fullWidth />
              <Button
                variant='outlined'
                sx={{
                  justifyContent: 'space-between',
                  flexGrow: 0,
                  minWidth: 0,
                  flexShrink: 0,
                  paddingX: '8px',
                }}
                onClick={onClickVideoPlay}
              >
                {t('details')}
              </Button>
            </>
          ) : (
            <Button
              variant='contained'
              endIcon={<PlayArrow />}
              sx={{ justifyContent: 'space-between', flexGrow: 1 }}
              onClick={onClickVideoPlay}
            >
              PLAY
            </Button>
          )}
        </>
      )}
      {isUpComing ? (
        <Button
          variant='contained'
          endIcon={<PlayArrow />}
          sx={{ justifyContent: 'space-between', flexGrow: 1 }}
          onClick={onClickAddPlaylist}
        >
          {t('add_to_playlist')}
        </Button>
      ) : (
        <IconButon onClick={onClickAddPlaylist}>
          <SvgAsset
            asset={isInPlaylist ? 'add_playlist_filled' : 'add_playlist'}
            sx={{ width: 32, height: 32, color: isInPlaylist ? Colors.yellow : Colors.color5A5957 }}
          />
          <Typography
            fontSize={12}
            lineHeight={1}
            fontWeight={500}
            sx={{ color: isInPlaylist ? Colors.yellow : Colors.color5A5957 }}
          >
            <Trans i18nKey='add' />
          </Typography>
        </IconButon>
      )}
      <IconButon onClick={() => setOpenShareModal(true)}>
        <SvgAsset asset='share' sx={{ width: 32, height: 32, fill: undefined }} />
        <Typography fontSize={12} lineHeight={1} fontWeight={500}>
          <Trans i18nKey='share' />
        </Typography>
      </IconButon>
      <ShareModal
        open={openShareModal}
        onClose={() => setOpenShareModal(false)}
        previewVideoId={videoPreviewModalInfo?.id ?? ''}
        title={videoPreviewModalInfo?.title ?? ''}
        description={videoPreviewModalInfo?.description ?? ''}
      />
    </Box>
  )
}
